import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import SchedulingStatusComponent from '../../components/SchedulingStatusComponent';
import Loading from '../../components/Loading';
import { RouteContext } from '../../context/router';
import getConfigs from '../../utils/config';
import CallMonitor from '../../components/CallMonitor';

import useStyles from './styles';
import { usePermissions } from '../../utils/permissions';

const SchedulingStatus = ({
  getStatus,
  getCallMessages,
  match,
  startProcess,
  checkinDriver,
  setRDSNumber,
  finishProcess,
  cancelProcess,
  statusLoading,
  callProcess,
  setAdditionalInfoCalled,
  setUpdateDriverPrimaryPhone,
  setUpdateDriverSecondaryPhone,
  schedulingDataToMonitor
}) => {

  const classes = useStyles();
  
  const history = useContext(RouteContext);
  const [schedulingData, setSchedulingData] = useState({});

  const [isEnableCalled, setEnableCalled] = useState(!!getConfigs()?.enable_scheduling_called);
  const [isEnableSendTruck, setEnableSendTruck] = useState(!!getConfigs()?.enable_scheduling_send_truck);
  const [isMonitorOpened, setMonitorOpened] = useState(false);

  const { scheduling: schedulingPermissions } = usePermissions();

  useEffect(() => {
    const fetchData = async () => {
      const schedulingId = match.params.id;
      const data = await getStatus(schedulingId);
      if (data) {
        setSchedulingData(data);
      }
    }
    fetchData();
  }, [getStatus, match]);

  useEffect(() => {
    const fetchData = async () => {
      const schedulingId = match.params.id;
      if(!!schedulingId) {
        await getCallMessages(schedulingId);
      }
    }

    if(schedulingPermissions.canCallMonitor){
      fetchData();
    }
    
  }, [getCallMessages, match, schedulingPermissions.canCallMonitor]);

  useEffect(() => {
    if (Object.keys(schedulingData).length > 0) {
      setEnableCalled(schedulingData?.branch_config?.enable_called);
      setEnableSendTruck(schedulingData?.branch_config?.enable_send_truck);
    }
  }, [schedulingData]);

  const handleStartProcess = async data => {
    const { started_date, id_scheduling_status } = await startProcess(data);
    setSchedulingData({ ...schedulingData, id_scheduling_status, started_date });
  }

  const handleDriverCheckin = async data => {
    const { driver_checkin_date, id_scheduling_status } = await checkinDriver(data);
    setSchedulingData({ ...schedulingData, id_scheduling_status, driver_checkin_date });
  }

  const handleAdditionalInfoCalled =  async data => {
    const { driver_checkin_date, id_scheduling_status, scheduling_status } = await setAdditionalInfoCalled(data);
    setSchedulingData({ ...schedulingData, id_scheduling_status, driver_checkin_date, scheduling_status });
  }

  const handleSetRDSNumber = async data => {
    const { rds_id, id_scheduling_status } = await setRDSNumber(data);
    setSchedulingData({ ...schedulingData, id_scheduling_status, rds_id });
  }

  const handleFinishProcess = async data => {
    const { ended_date, id_scheduling_status } = await finishProcess(data);
    setSchedulingData({ ...schedulingData, id_scheduling_status, ended_date });
  }

  const handleCancelProcess = async (id, reason) => {
    const { cancelled_date, id_scheduling_status, cancelled_reason } = await cancelProcess({ id, reason });
    setSchedulingData({ ...schedulingData, id_scheduling_status, cancelled_date, cancelled_reason });
  }

  const handleCallProcess = async (id) => {
    const { id_scheduling_status, called_at } = await callProcess(id);
    setSchedulingData({ ...schedulingData, id_scheduling_status, called_at });
  }

  const handleMonitorOpen = () => {
    getCallMessages(schedulingDataToMonitor?.id)
    setMonitorOpened(true);
  }

  const handleBackToList = () => history.push('/agendamento');

  return (
    <div className={classes.root}>
      <CallMonitor
        monitorControl={isMonitorOpened}
        setMonitorOpened={setMonitorOpened}
        schedulingData={{...schedulingDataToMonitor}}
        onCalled={handleCallProcess}
        setUpdateDriverPrimaryPhone={setUpdateDriverPrimaryPhone}
        setUpdateDriverSecondaryPhone={setUpdateDriverSecondaryPhone}
        getCallMessages={getCallMessages}
        id={`call-monitor-scheduling-status-id`}
        data-cy={`call-monitor-scheduling-status-data-cy`}
      />
      {statusLoading ? <Loading /> : (
        <SchedulingStatusComponent
          schedulingData={schedulingData}
          enableCalled={isEnableCalled}
          enableSendTruck={isEnableSendTruck}
          onBackToList={handleBackToList}
          startProcess={handleStartProcess}
          setDriverCheckin={handleDriverCheckin}
          setRDS={handleSetRDSNumber}
          finishProcess={handleFinishProcess}
          cancelProcess={handleCancelProcess}
          callProcess={handleCallProcess}
          setAdditionalInfoCalled={handleAdditionalInfoCalled}
          handleMonitorOpen={handleMonitorOpen}
          id={`scheduling-status-component-id`}
          data-cy={`cheduling-status-component-data-cy`}
        />
      )}
    </div>
  );
};

const mapStateToProps = ({ branches: { branches }, scheduling: { loading, schedulingData } }) => ({
  branches: branches.map((branch) => ({
    value: branch.id,
    label: branch.name,
  })),
  statusLoading: loading,
  schedulingDataToMonitor: schedulingData
});

const mapDispatch = (dispatch) => ({
  getStatus: (payload) => dispatch.scheduling.getStatus(payload),
  getCallMessages: (payload) => dispatch.scheduling.getCallMessages(payload),
  startProcess: (payload) => dispatch.scheduling.start(payload),
  checkinDriver: (payload) => dispatch.scheduling.checkinDriver(payload),
  setRDSNumber: (payload) => dispatch.scheduling.setRDSNumber(payload),
  finishProcess: (payload) => dispatch.scheduling.finish(payload),
  cancelProcess: (payload) => dispatch.scheduling.cancel(payload),
  callProcess: (payload) => dispatch.scheduling.calledAsync(payload),
  setAdditionalInfoCalled: (payload) => dispatch.scheduling.saveAdditionalInfoCalled(payload),
  setUpdateDriverPrimaryPhone: (payload) => dispatch.scheduling.updateDriverPrimaryPhone(payload),
  setUpdateDriverSecondaryPhone: (payload) => dispatch.scheduling.updateDriverSecondaryPhone(payload)
});

SchedulingStatus.propTypes = {
  branches: PropTypes.array.isRequired,
  getStatus: PropTypes.func.isRequired,
  startProcess: PropTypes.func.isRequired,
  checkinDriver: PropTypes.func.isRequired,
  setRDSNumber: PropTypes.func.isRequired,
  finishProcess: PropTypes.func.isRequired,
  cancelProcess: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatch)(withRouter(SchedulingStatus));
